// Boost Blenz Admin — app shell (sidebar nav, topbar, theme toggle, routing).
// Ported from the prototype's admin-app.jsx. The prototype's own login screen is
// NOT ported: Cloudflare Access gates every request at the edge, and /api/me
// returns the signed-in identity + role. Sign-out hands off to Access logout.

import React from "react";
import { OSWALD, FIGTREE, THEME, ThemeContext, useT, useIsMobile } from "./theme";
import { Icon, Card, Title, SectionTitle } from "./atoms";
import { BrandMark } from "./BrandMark";
import { ROLE_LABELS } from "./permissions";
import { BBAdminData } from "./data/admin";
import { BBFin } from "./data/finance";
import { BB_HELP } from "./data/help";
import { OverviewView, ProfitView, UnitView, AllocateView, ExpensesView, ReplacementsView, EmailMarketingView, IntegrationsView, SecurityView, useSystemStatus, sinceLabel } from "./views/finance";
import { AdsView } from "./views/paidads";
import { SeoView } from "./views/seo";
import { NewStuffView } from "./views/newstuff";
import { KpiTargetsView } from "./views/kpi";
import { DecisionDashboardView } from "./views/decision";
import { ForecastingView } from "./views/forecasting";
import { BooksView } from "./views/books";
import { InsightsView } from "./views/insights";
import { QuizView } from "./views/quiz";
import { Avatar } from "./views/appviews";
// App section v2 (Claude Design handoff, 2026-09-13): App Health + drill-downs, Customers + the
// full profile, Messaging. All on the Detox Tracker's real database (worker/appinsights.ts).
import { HealthOverview, PeopleProgress, DosesBinder, Reminders, GettingStarted, Questions } from "./views/app/health";
import { CustomersList, CustomerProfile, GlobalSearch } from "./views/app/customers";
import { Messaging } from "./views/app/messaging";
import { useCachedJson } from "./datacache";
import { AppCopyView } from "./views/appcopy";
import { HelpDeskView, KnowledgeBaseView } from "./views/support";
import { UsersView } from "./views/users";

// permission key that gates each nav id (screens map 1:1; Users → manage_users)
/**
 * Which permission a nav item needs. Most are their own id.
 *
 * 🔴 `releases` borrows `push` rather than getting its own, and the worker routes behind it
 * require `push` too — the two must agree, or the screen shows for someone the server then
 * refuses. A new nav id with no entry here resolves to a permission nobody has, so the item is
 * silently hidden from everyone, owners included.
 */
const permKeyOf = (id: string) => (id === "users" ? "manage_users" : id);

/**
 * EVERY SCREEN HAS A URL (SPEC §3). Hash routes, so Cloudflare Access and the Worker's static
 * serving need no changes and a pasted link always reaches the SPA.
 *
 *   #/app/health[/people|doses|reminders|getting-started|questions]
 *   #/app/customers[?filter=needs|all|detoxing|waiting|break|finished|never]
 *   #/app/customers/<id>
 *   #/app/messaging[?tab=catalogue]      #/app/copy
 *   #/support/<account>/<threadId>       #/<navId> for everything else
 *
 * The last screen is still remembered in localStorage, but only as the landing page when the URL
 * has no hash; the URL always wins.
 */
const ROUTE_OF: Record<string, string> = {
  health: "#/app/health", customers: "#/app/customers", messaging: "#/app/messaging", appcopy: "#/app/copy",
  revenue: "#/app/in-app-sales", content: "#/app/content", helpdesk: "#/support",
};
const APP_SEG: Record<string, string> = { health: "health", customers: "customers", messaging: "messaging", copy: "appcopy", "in-app-sales": "revenue", content: "content" };
const routeOf = (id: string) => ROUTE_OF[id] || "#/" + id;
type Route = { view: string; parts: string[]; query: URLSearchParams; path: string };
function parseRoute(hash: string): Route | null {
  const raw = hash.replace(/^#\/?/, "");
  if (!raw) return null;
  const [path, qs] = raw.split("?");
  const parts = path.split("/").filter(Boolean).map(decodeURIComponent);
  const query = new URLSearchParams(qs || "");
  let view = parts[0];
  if (parts[0] === "app") view = APP_SEG[parts[1]] || "health";
  else if (parts[0] === "support") view = "helpdesk";
  // Old ids from before Messaging merged them.
  if (view === "push" || view === "releases") view = "messaging";
  return { view, parts, query, path };
}
function useHashRoute(fallbackView: string) {
  const [hash, setHash] = React.useState(() => window.location.hash);
  React.useEffect(() => {
    if (!window.location.hash) {
      const h = routeOf(fallbackView);
      window.history.replaceState(null, "", h);
      setHash(h);
    }
    const on = () => setHash(window.location.hash);
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return parseRoute(hash) ?? parseRoute(routeOf(fallbackView))!;
}

/** App-section items a role can't open stay in the nav, dimmed with a padlock (SPEC §5.12). */
const APP_SECTION = new Set(["health", "customers", "revenue", "content", "appcopy", "messaging", "helpdesk"]);

// Placeholder view for modules whose scope is locked but the build is deferred.
// Keeps the section in the product and documents what it will do.
function ComingSoonView({ title, icon, tagline, scope }: any) {
  const t = useT();
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <SectionTitle sub={tagline}>{title}</SectionTitle>
      <Card pad={0}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '44px 24px 30px', borderBottom: '1px solid ' + t.border }}>
          <span style={{ width: 60, height: 60, borderRadius: 16, background: t.surface2, border: '1px solid ' + t.border, display: 'grid', placeItems: 'center', marginBottom: 16 }}>
            <Icon name={icon} size={26} color={t.accent} />
          </span>
          <span style={{ fontFamily: OSWALD, fontWeight: 600, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.accent }}>Coming soon</span>
          <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 24, textTransform: 'uppercase', letterSpacing: '0.02em', color: t.fg, marginTop: 8 }}>{title} is on the roadmap</div>
          <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 8, maxWidth: 480, lineHeight: 1.55 }}>Scope is locked and this section stays in the product. We'll build it out in a later phase.</div>
        </div>
        <div style={{ padding: '22px 24px' }}>
          <div style={{ fontFamily: OSWALD, fontWeight: 600, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.muted, marginBottom: 14 }}>Planned scope</div>
          <div style={{ display: 'grid', gap: 12 }}>
            {scope.map((s: string, i: number) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ width: 22, height: 22, borderRadius: 999, flexShrink: 0, background: t.surface2, border: '1px solid ' + t.border, display: 'grid', placeItems: 'center', fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, color: t.muted, marginTop: 1 }}>{i + 1}</span>
                <span style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.fg2, lineHeight: 1.55 }}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

// Topbar status bell — alert icon with a count badge + a dropdown of the CURRENT
// active integration issues. Driven by the same live /api/status engine as the
// Daily Pulse Morning Brief (so they never disagree); "view all" jumps to the
// Brief for the full plain-English fix + duration.
function IncidentsBell({ status, go, t }: any) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<any>(null);
  React.useEffect(() => {
    if (!open) return;
    const h = (e: any) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const issues: any[] = status?.issues || [];
  const count = issues.length;
  const anyDown = issues.some((i) => i.severity === 'down');
  const badgeColor = anyDown ? t.bad : count ? t.warn : t.muted;
  const sevColor = (s: string) => (s === 'down' ? t.bad : t.warn);
  const viewAll = () => { setOpen(false); go('pulse'); };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} title={(count || 'No') + ' active issue' + (count === 1 ? '' : 's')} style={{
        position: 'relative', width: 38, height: 38, borderRadius: 999, border: '1px solid ' + t.border,
        background: t.surface2, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon name="alert" size={18} color={badgeColor} />
        {count > 0 && (
          <span style={{ position: 'absolute', top: -4, right: -4, minWidth: 17, height: 17, padding: '0 4px', borderRadius: 999, background: badgeColor, color: '#fff', fontFamily: FIGTREE, fontWeight: 700, fontSize: 10.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '2px solid ' + t.surface }}>{count}</span>
        )}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '120%', right: 0, zIndex: 50, width: 340, maxWidth: '86vw', background: t.surface, border: '1px solid ' + t.borderStrong, borderRadius: t.cardRadius || 8, boxShadow: '0 12px 32px rgba(0,0,0,0.22)', overflow: 'hidden' }}>
          <div style={{ padding: '11px 14px', borderBottom: '1px solid ' + t.border, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: t.faint }}>System status</span>
            <span style={{ marginLeft: 'auto', fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, color: count ? badgeColor : t.good }}>{status == null ? 'Checking…' : count ? count + ' active' : 'All clear'}</span>
          </div>
          {status == null ? (
            <div style={{ padding: '18px 14px', fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Checking integrations…</div>
          ) : count === 0 ? (
            <div style={{ padding: '16px 14px', display: 'flex', gap: 9, alignItems: 'center' }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: t.good, flexShrink: 0 }} />
              <span style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2 }}>All systems operational.</span>
            </div>
          ) : issues.map((iss: any) => (
            <button key={iss.id} onClick={viewAll} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none', borderTop: '1px solid ' + t.border, background: 'transparent', cursor: 'pointer',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, marginTop: 5, flexShrink: 0, background: sevColor(iss.severity) }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontFamily: FIGTREE, fontWeight: 600, fontSize: 13, color: t.fg, lineHeight: 1.35 }}>{iss.title}</span>
                <span style={{ display: 'block', fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, marginTop: 2 }}>{iss.source} · {iss.severity} · {sinceLabel(iss.since)}</span>
              </span>
            </button>
          ))}
          {count > 0 && (
            <button onClick={viewAll} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '11px 14px', border: 'none', borderTop: '1px solid ' + t.border, background: t.surface2, cursor: 'pointer', fontFamily: FIGTREE, fontWeight: 700, fontSize: 11.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: t.claude }}>
              View all on Daily Pulse <Icon name="chevron" size={13} color={t.claude} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Sidebar nav item — hover = subtle purple fill; active = orange-tinted bg,
// orange border and a 3px orange left rail (per the Daily Pulse redesign handoff).
function NavBtn({ n, active, soon, badge, t, onClick, locked }: any) {
  const [h, setH] = React.useState(false);
  return (
    <button onClick={locked ? undefined : onClick} title={locked ? 'Your role cannot open this' : undefined} aria-disabled={locked || undefined} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{
      display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', borderRadius: 11,
      textAlign: 'left', width: '100%', position: 'relative', opacity: locked ? 0.45 : 1, cursor: locked ? 'not-allowed' : 'pointer',
      border: '1px solid ' + (active ? 'rgba(248,146,92,0.35)' : 'transparent'),
      background: active ? 'rgba(248,146,92,0.13)' : h ? (soon ? 'rgba(150,130,230,0.08)' : 'rgba(150,130,230,0.12)') : 'transparent',
      color: active ? t.fg : soon ? '#7a7099' : h ? t.fg : t.sidebarMuted,
      fontFamily: FIGTREE, fontWeight: active ? 700 : 500, fontSize: 14,
      transition: 'background 160ms, color 160ms',
    }}>
      {active && <span style={{ position: 'absolute', left: 0, top: 9, bottom: 9, width: 3, borderRadius: 999, background: t.accent }} />}
      <Icon name={n.icon} size={16} color={active ? t.accent : 'currentColor'} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block' }}>{n.label}</span>
        {n.sub && !locked && <span style={{ display: 'block', fontFamily: FIGTREE, fontWeight: 500, fontSize: 10.5, color: t.sidebarMuted, marginTop: 1 }}>{n.sub}</span>}
      </span>
      {locked && <Icon name="lock" size={13} color={t.sidebarMuted} />}
      {soon ? <span style={{ border: '1px solid rgba(150,130,230,0.28)', color: t.sidebarMuted, fontFamily: OSWALD, fontWeight: 700, fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 999 }}>Soon</span> : null}
      {badge ? <span style={{ background: t.accent, color: '#1a1330', fontFamily: FIGTREE, fontWeight: 800, fontSize: 11, minWidth: 20, height: 20, padding: '0 4px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{badge}</span> : null}
    </button>
  );
}

const NAV = [
  { section: 'Overview', items: [
    { id: 'pulse', label: 'Daily Pulse', icon: 'pulse' },
    { id: 'decision', label: 'Decision Dashboard', icon: 'grid' },
    { id: 'forecasting', label: 'Forecasting', icon: 'chart' },
    { id: 'books', label: 'Books', icon: 'doc' },
    { id: 'insights', label: 'Insights', icon: 'bullseye' },
  ] },
  { section: 'Finances', items: [
    { id: 'profit', label: 'Profit', icon: 'dollar' },
    { id: 'unit', label: 'Per-Unit Cost', icon: 'box' },
    { id: 'allocate', label: 'Profit-First', icon: 'percent' },
    { id: 'expenses', label: 'Expenses', icon: 'receipt' },
    { id: 'replacements', label: 'Replacements', icon: 'dollarslash' },
    { id: 'newstuff', label: 'New Stuff', icon: 'spark' },
  ] },
  { section: 'Marketing', items: [
    { id: 'ads', label: 'Paid Ads', icon: 'chart' },
    { id: 'seo', label: 'SEO', icon: 'search' },
    { id: 'email', label: 'Email Marketing', icon: 'mail' },
    { id: 'quiz', label: 'Quiz', icon: 'quiz' },
    { id: 'kpi', label: 'KPI Targets', icon: 'percent' },
  ] },
  { section: 'App', items: [
    { id: 'health', label: 'App Health', icon: 'health' },
    { id: 'customers', label: 'Customers', icon: 'users' },
    { id: 'revenue', label: 'In-App Sales', icon: 'store' },
    { id: 'content', label: 'Content', icon: 'doc' },
    { id: 'appcopy', label: 'App Copy', icon: 'tag' },
    // Releases & notices + Push & Email, merged (SPEC §5.11): "what we send". App Copy stays its
    // own item — it is "what the app says", an editor, not a broadcast tool.
    { id: 'messaging', label: 'Messaging', icon: 'bell', sub: 'Notices · Push & email' },
    // 🔴 NO "Suppression" ITEM. It was a screen of per-category toggles over mock data that
    // wrote nothing and that nothing honoured — and a customer's real off switch is a fact
    // about that customer, not a list of its own. It lives on their record under Customers
    // (Dustin, 2026-09-10: "suppression makes sense to move to each customer… merge them").
  ] },
  // Inventory Hub is its own app (boost-blenz-erp). Clicking opens it in a new tab; Cloudflare
  // Access's session carries over, so there's no second sign-in code.
  { section: 'Operations', items: [
    { id: 'inventory', label: 'Inventory Hub', icon: 'box', sub: 'Batches · lots · purchasing ↗' },
  ] },
  { section: 'Customer Service', items: [
    { id: 'helpdesk', label: 'Help Desk', icon: 'mail' },
    { id: 'kb', label: 'Knowledge Base', icon: 'doc' },
  ] },
  { section: 'Settings', items: [
    { id: 'integrations', label: 'Integrations', icon: 'plug' },
    { id: 'security', label: 'Security', icon: 'lock' },
    { id: 'users', label: 'Users', icon: 'users' },
  ] },
];
const NAV_ITEMS = NAV.flatMap((g) => g.items);
const ERP_URL = 'https://erp.boostblenzhq.com';

// Live count of customer emails needing a response — drives the Help Desk sidebar
// badge (was hard-coded). Cheap: served from the cached inbox list, polled every 60s.
function useHelpdeskWaiting(enabled: boolean) {
  const [n, setN] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (!enabled) return; // no `helpdesk` permission — the API would refuse anyway
    let live = true;
    const load = () => fetch('/api/helpdesk/count')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (live && d) setN(Number(d.needs) || 0); })
      .catch(() => {});
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => { live = false; clearInterval(id); };
  }, [enabled]);
  return n;
}

function Dashboard({ session, onSignOut }: any) {
  const theme = THEME; // single dark-purple scheme (owner: no light/dark toggle)
  const route = useHashRoute(localStorage.getItem('bb-admin-view') || 'pulse');
  const view = route.view;
  const [range, setRangeState] = React.useState(() => localStorage.getItem('bb-app-range') || '30d');
  const setRange = (r: string) => { setRangeState(r); try { localStorage.setItem('bb-app-range', r); } catch { /* private mode */ } };
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = React.useState(false);
  const sysStatus = useSystemStatus(); // live /api/status — feeds the topbar status bell (all pages)
  const hdWaiting = useHelpdeskWaiting(!session?.permissions || session.permissions.includes('helpdesk')); // live count of customer emails needing a response — sidebar badge
  React.useEffect(() => { setNavOpen(false); }, [route.path]); // close the drawer after navigating

  React.useEffect(() => { localStorage.setItem('bb-admin-view', view); }, [view]);
  // A new page starts at the top; a filter or tab change on the same page doesn't jump.
  React.useEffect(() => { window.scrollTo(0, 0); }, [route.path]);

  /** Navigate by nav id ("customers") or by URL ("#/app/customers/abc"). */
  function go(v: string) {
    const h = v.startsWith('#') ? v : routeOf(v);
    if (window.location.hash !== h) window.location.hash = h;
  }

  function handleJump(ref: any) {
    if (!ref) return;
    if (ref === 'segment' || ref === 'push' || ref === 'releases') {
      go('messaging');
    } else if (ref === 'suppression') {
      go('customers'); // the switch moved onto the customer's own record
    } else if (NAV_ITEMS.some((n) => n.id === ref)) {
      go(ref); // any plain nav id (unit, email, allocate, integrations, kpi, …)
    }
  }

  const t = theme;
  // Permission gating: session.permissions comes from /api/me. In local dev
  // (no Worker → session is null) we don't gate, so everything shows.
  const perms: string[] | null = (session && session.permissions) || null;
  const can = (key: string) => !perms || perms.includes(key);
  // Messaging is readable with `push` OR `customers` (Support reads it; posting still needs push).
  const canNav = (id: string) => (id === 'messaging' ? can('push') || can('customers') : can(permKeyOf(id)));
  const allowedItems = NAV_ITEMS.filter((n) => canNav(n.id));
  const known = NAV_ITEMS.some((n) => n.id === view);
  // A direct URL to an App-section screen this role can't open shows a plain card, never a blank
  // screen or a silent redirect. Anything else unknown or forbidden falls back to the first allowed.
  const lockedView = known && !canNav(view) && APP_SECTION.has(view);
  const activeView = known && (canNav(view) || lockedView) ? view : (allowedItems[0]?.id || view);
  React.useEffect(() => { if (activeView !== view) go(activeView); }, [activeView, view]);
  const curNav = NAV_ITEMS.find((n) => n.id === activeView) || NAV_ITEMS[0];
  // Needs-a-person count for the App Health badge — the same people payload Customers uses.
  const people = useCachedJson<any>(can('customers') ? '/api/app/people' : null);
  const needsCount = (people.data?.people || []).filter((p: any) => p.needs?.length).length;
  const iconBtn: any = { width: 38, height: 38, borderRadius: 999, border: '1px solid ' + t.border, background: t.surface2, color: t.fg2, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
  const asideStyle: any = isMobile
    ? { width: 272, background: t.sidebar, color: t.sidebarFg, display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 80, transform: navOpen ? 'translateX(0)' : 'translateX(-110%)', transition: 'transform 240ms cubic-bezier(0.2,0.7,0.2,1)', boxShadow: navOpen ? '0 20px 60px rgba(0,0,0,0.6)' : 'none', borderRight: '1px solid ' + t.border }
    : { width: 244, flexShrink: 0, background: t.sidebar, color: t.sidebarFg, display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', borderRight: '1px solid ' + t.border };

  return (
    <ThemeContext.Provider value={theme}>
      <div style={{ display: 'flex', minHeight: '100vh', background: t.appBg, color: t.fg, fontFamily: FIGTREE }}>
        {isMobile && navOpen && <div onClick={() => setNavOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(6,4,18,0.55)', zIndex: 75 }} />}
        {/* SIDEBAR */}
        <aside style={asideStyle}>
          <div style={{ padding: '22px 20px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <BrandMark size={34} />
            <div>
              <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 16, letterSpacing: '0.03em', textTransform: 'uppercase', color: t.sidebarFg, lineHeight: 1 }}>Boost Blenz</div>
              <div style={{ fontFamily: FIGTREE, fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.sidebarMuted, marginTop: 3 }}>Super Admin</div>
            </div>
          </div>

          <nav style={{ padding: '4px 16px 24px', display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflowY: 'auto' }}>
            {NAV.map((group) => {
              const items = group.items.filter((n) => canNav(n.id) || APP_SECTION.has(n.id));
              if (!items.length) return null;
              return (
              <div key={group.section} style={{ marginTop: 14 }}>
                <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: t.faint, padding: '0 12px 6px' }}>{group.section}</div>
                {items.map((n) => {
                  const active = activeView === n.id;
                  const soon = n.id === 'revenue' || n.id === 'content';
                  const locked = !canNav(n.id);
                  const badge = locked ? null : n.id === 'health' ? (needsCount || null) : n.id === 'helpdesk' ? (hdWaiting || null) : null;
                  return <NavBtn key={n.id} n={n} active={active} soon={soon} badge={badge} locked={locked} t={t} onClick={() => (n.id === 'inventory' ? window.open(ERP_URL, '_blank', 'noopener') : go(n.id))} />;
                })}
              </div>
              );
            })}
          </nav>
        </aside>

        {/* MAIN — static textured deep-purple backdrop; cards scroll over it.
            backgroundAttachment:fixed keeps the texture still under body scroll
            (iOS ignores it and scrolls the texture — acceptable fallback). */}
        <div style={{
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
          backgroundColor: t.mainBg,
          backgroundImage: 'repeating-linear-gradient(118deg, rgba(255,255,255,0.02) 0 1px, transparent 1px 26px), radial-gradient(130% 80% at 55% -6%, #241d52 0%, #17122f 46%, #100c24 100%)',
          backgroundAttachment: 'fixed, fixed',
        }}>
          {/* topbar */}
          <header style={{ position: 'sticky', top: 0, zIndex: 40, background: 'rgba(13,10,30,0.72)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', borderBottom: '1px solid ' + t.border, padding: isMobile ? '0 14px' : '0 28px', height: 64, display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 16 }}>
            {isMobile && (
              <button onClick={() => setNavOpen(true)} aria-label="Open menu" style={iconBtn}>
                <Icon name="menu" size={20} color={t.fg2} />
              </button>
            )}
            <Title size={isMobile ? 16 : 19} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{curNav.label}</Title>
            {!isMobile && can('customers') && <GlobalSearch go={go} />}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 }}>
              <IncidentsBell status={sysStatus} go={go} t={t} />
              <AccountMenu session={session} onSignOut={onSignOut} isMobile={isMobile} t={t} />
            </div>
          </header>

          <main style={{ padding: isMobile ? '18px 15px 44px' : '28px 40px 48px', maxWidth: 1500, width: '100%', boxSizing: 'border-box' }}>
            {activeView === 'pulse' && <OverviewView onJump={handleJump} />}
            {activeView === 'profit' && <ProfitView />}
            {activeView === 'decision' && <DecisionDashboardView />}
            {activeView === 'forecasting' && <ForecastingView />}
            {activeView === 'books' && <BooksView />}
            {activeView === 'insights' && <InsightsView />}
            {activeView === 'unit' && <UnitView />}
            {activeView === 'allocate' && <AllocateView />}
            {activeView === 'expenses' && <ExpensesView />}
            {activeView === 'replacements' && <ReplacementsView />}
            {activeView === 'newstuff' && <NewStuffView />}
            {activeView === 'ads' && <AdsView />}
            {activeView === 'seo' && <SeoView />}
            {activeView === 'email' && <EmailMarketingView />}
            {activeView === 'quiz' && <QuizView />}
            {activeView === 'kpi' && <KpiTargetsView />}
            {isMobile && can('customers') && APP_SECTION.has(activeView) && <div style={{ marginBottom: 14, display: 'flex' }}><GlobalSearch go={go} /></div>}
            {lockedView ? (
              <Card pad={0}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10, padding: '40px 22px' }}>
                  <Icon name="lock" size={24} color={t.muted} />
                  <Title size={17}>Your role cannot open {curNav.label}</Title>
                  <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, maxWidth: 460, lineHeight: 1.55 }}>
                    {curNav.id === 'appcopy'
                      ? 'App Copy changes what the app says to every customer, so it stays with Ops and Owner. Support can read every app number and open every customer.'
                      : 'Ask the owner to turn it on for you in Settings → Users if you need it.'}
                  </div>
                </div>
              </Card>
            ) : (<>
            {activeView === 'health' && (() => {
              const sub = route.parts[2];
              const P = { go, range, setRange };
              return sub === 'people' ? <PeopleProgress {...P} /> : sub === 'doses' ? <DosesBinder {...P} /> : sub === 'reminders' ? <Reminders {...P} />
                : sub === 'getting-started' ? <GettingStarted {...P} /> : sub === 'questions' ? <Questions {...P} /> : <HealthOverview {...P} />;
            })()}
            {activeView === 'customers' && (route.parts[2]
              ? <CustomerProfile key={route.parts[2]} id={route.parts[2]} go={go} />
              : <CustomersList go={go} filter={route.query.get('filter') || 'needs'} />)}
            {activeView === 'revenue' && <ComingSoonView title="In-App Sales" icon="store" tagline="Post-purchase revenue inside the app — books, kit re-orders, and upsells." scope={['One-off digital sales (books / guides) surfaced from the Shopify product catalog', 'Kit re-order & subscription renewal tracking per customer', 'Upsell + cross-sell attribution (which in-app prompt drove the sale)', 'Revenue-per-active-user and LTV rollups feeding Daily Pulse & Profit']} />}
            {activeView === 'messaging' && <Messaging tab={route.query.get('tab') || 'notices'} go={go} />}
                        {activeView === 'content' && <ComingSoonView title="Content" icon="doc" tagline="The library of articles and books served inside the app." scope={['Articles pulled live from the Shopify blog (Online Store → Blog posts) — publish/schedule in Shopify, flows straight to the app', 'Books synced from Shopify products; readable asset (PDF / EPUB) uploaded once', 'Content delivery & entitlements — one uploaded book reaches every paying reader, access turns off on refund', 'Read-through & engagement metrics per piece']} />}
            {activeView === 'appcopy' && <AppCopyView />}
            {activeView === 'helpdesk' && <HelpDeskView go={go} deepLink={route.parts[2] ? { account: route.parts[1], threadId: route.parts[2] } : null} />}
            {activeView === 'kb' && <KnowledgeBaseView />}
            {activeView === 'integrations' && <IntegrationsView />}
            {activeView === 'security' && <SecurityView />}
            {activeView === 'users' && <UsersView me={session} />}
            </>)}
          </main>
        </div>

      </div>
    </ThemeContext.Provider>
  );
}

// Topbar account menu — shows who's signed in + sign out.
function AccountMenu({ session, onSignOut, isMobile, t }: any) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<any>(null);
  React.useEffect(() => {
    if (!open) return;
    const h = (e: any) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const email = (session && session.email) || 'ops@boostblenz.com';
  const role = ROLE_LABELS[(session && session.role) as keyof typeof ROLE_LABELS] || (session && session.role) || 'Owner';
  const method = session && session.method === 'passkey' ? 'Passkey' : '2FA verified';
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', border: 'none', background: 'transparent',
        padding: isMobile ? 0 : '2px 4px 2px 12px', borderLeft: isMobile ? 'none' : '1px solid ' + t.border,
      }}>
        <Avatar name={email} size={30} />
        {!isMobile && <span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 13, color: t.fg2 }}>{role}</span>}
        {!isMobile && <Icon name="chevron" size={13} color={t.faint} style={{ transform: open ? 'rotate(-90deg)' : 'rotate(90deg)' }} />}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '120%', right: 0, zIndex: 60, width: 244, background: t.surface, border: '1px solid ' + t.borderStrong, borderRadius: t.cardRadius || 8, boxShadow: '0 12px 32px rgba(0,0,0,0.22)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 15px', borderBottom: '1px solid ' + t.border, display: 'flex', gap: 11, alignItems: 'center' }}>
            <Avatar name={email} size={38} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</div>
              <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted }}>{role}</div>
            </div>
          </div>
          <div style={{ padding: '10px 15px', borderBottom: '1px solid ' + t.border, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 7, height: 7, borderRadius: 999, background: t.good, flexShrink: 0 }} />
            <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>{method} · session active</span>
          </div>
          <button onClick={() => { setOpen(false); onSignOut && onSignOut(); }} style={{
            display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', padding: '12px 15px', border: 'none',
            background: 'transparent', cursor: 'pointer', fontFamily: FIGTREE, fontWeight: 600, fontSize: 13, color: t.bad,
          }}>
            <Icon name="lock" size={15} color={t.bad} />Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// Minimal boot splash while /api/me resolves (Cloudflare Access has already
// authenticated the request by the time this renders).
function BootScreen() {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0d0a1e', color: '#f4f1ff', fontFamily: FIGTREE }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <BrandMark size={34} />
        <span style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 18, letterSpacing: '0.03em', textTransform: 'uppercase' }}>Boost Blenz Admin</span>
      </div>
    </div>
  );
}

type Me = { email: string; role: string | null; permissions?: string[] };

// Root: resolve identity from /api/me (behind Cloudflare Access), then render the
// dashboard. Sign-out hands off to the Access logout endpoint.
export function App() {
  const [me, setMe] = React.useState<Me | null>(null);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    // Resolve identity + hydrate the data singletons from the Worker in parallel.
    // Object.assign mutates the singletons in place (identity preserved, so views
    // keep reading them synchronously and function members like relTime survive).
    // If bootstrap is unavailable (e.g. local dev without the Worker), we keep the
    // bundled mock — the UI is identical either way.
    const meReq = fetch("/api/me").then((r) => (r.ok ? r.json() : null)).catch(() => null);
    const bootReq = fetch("/api/bootstrap").then((r) => (r.ok ? r.json() : null)).catch(() => null);
    Promise.all([meReq, bootReq]).then(([m, boot]) => {
      if (boot) {
        if (boot.admin) Object.assign(BBAdminData, boot.admin);
        if (boot.finance) Object.assign(BBFin, boot.finance);
        if (boot.help) Object.assign(BB_HELP, boot.help);
      }
      setMe(m);
      setLoaded(true);
    });
  }, []);

  if (!loaded) return <BootScreen />;

  const session = me ? { email: me.email, role: me.role, permissions: me.permissions, method: "2FA verified" } : null;
  const onSignOut = () => { window.location.href = "/cdn-cgi/access/logout"; };

  return <Dashboard session={session} onSignOut={onSignOut} />;
}
